import {
  DeleteResource,
  DeleteResourceImage,
  GetResource,
  GetResourceHierarchy,
  GetResourceImage,
  GetResourceImageUploadUrl,
  GetResources,
  PostCompleteResourceImageUpload,
  PostResource,
} from "@/server-types";
import { keepPreviousData } from "@tanstack/react-query";
import { del, get, post } from "../request-helpers";
import { useCustomQuery } from "../custom-query";
import { useCustomMutation } from "../custom-mutation";
import { queryClient } from "..";

export const useGetResources = ({
  resourceGroupId,
  locationId,
  clientId,
}: {
  resourceGroupId?: number | undefined;
  locationId?: number | undefined;
  clientId?: number | undefined;
}) => {
  return useCustomQuery({
    queryKey: [resourceGroupId, "resources", locationId, clientId],
    queryFn: () =>
      get<GetResources>(`/resource`, {
        resourceGroupId,
        locationId,
        clientId,
      }),
    placeholderData: keepPreviousData,
    // enabled: resourceGroupId != null && resourceGroupId !== -1,
    enabled: locationId != -1,
    gcTime: 0,
  });
};

export const usePostResource = () => {
  return useCustomMutation({
    mutationKey: ["resources"],
    mutationFn: (resource: PostResource["body"]) =>
      post<PostResource>("/resource", resource),
  });
};

export const useGetResource = (resourceId: number | undefined) => {
  return useCustomQuery({
    queryKey: ["resources", resourceId],
    queryFn: () => get<GetResource>(`/resource/${resourceId ?? -1}`),
    placeholderData: keepPreviousData,
    enabled: resourceId != null && resourceId !== -1,
  });
};

export const useDeleteResource = () => {
  return useCustomMutation({
    mutationKey: ["resources"],
    mutationFn: (resourceId: number) =>
      del<DeleteResource>(`/resource/${resourceId}`),
    displayNotification: true,
  });
};

export const useGetResourceHierarchy = () => {
  return useCustomQuery({
    queryKey: ["resource-hierarchy"],
    queryFn: () => get<GetResourceHierarchy>(`/resource/hierarchy`),
    placeholderData: keepPreviousData,
  });
};

/**
 * The resource lists render `imageUrl`, so an image change has to drop them
 * too. Their query keys don't share a stable prefix (`useGetResources` keys on
 * the group id first), hence the predicate rather than a `queryKey` match.
 */
function invalidateResourceImageQueries(resourceId: number) {
  queryClient.invalidateQueries({
    queryKey: ["resourceImage", resourceId],
  });
  queryClient.invalidateQueries({
    predicate: (query) => query.queryKey.includes("resources"),
  });
}

/**
 * GET /resource/:resourceId/image
 */
export const useGetResourceImage = (resourceId: number | undefined) => {
  return useCustomQuery({
    queryKey: ["resourceImage", resourceId],
    queryFn: () => get<GetResourceImage>(`/resource/${resourceId ?? -1}/image`),
    enabled: resourceId != null && resourceId > 0,
  });
};

/**
 * GET /resource/:resourceId/image/upload
 */
export const useGetResourceImageUploadUrl = () => {
  return useCustomMutation({
    mutationKey: ["getResourceImageUploadUrl"],
    mutationFn: ({
      resourceId,
      query,
    }: {
      resourceId: number;
      query: GetResourceImageUploadUrl["query"];
    }) =>
      get<GetResourceImageUploadUrl>(
        `/resource/${resourceId}/image/upload`,
        query,
      ),
  });
};

/**
 * POST /resource/:resourceId/image/upload/complete
 */
export const useCompleteResourceImageUpload = () => {
  return useCustomMutation({
    mutationKey: ["completeResourceImageUpload"],
    mutationFn: ({
      resourceId,
      body,
    }: {
      resourceId: number;
      body: PostCompleteResourceImageUpload["body"];
    }) =>
      post<PostCompleteResourceImageUpload>(
        `/resource/${resourceId}/image/upload/complete`,
        body,
      ),
    onSuccess: (_response, variables) => {
      invalidateResourceImageQueries(variables.resourceId);
    },
  });
};

/**
 * DELETE /resource/:resourceId/image
 */
export const useDeleteResourceImage = () => {
  return useCustomMutation({
    mutationFn: ({ resourceId }: { resourceId: number }) =>
      del<DeleteResourceImage>(`/resource/${resourceId}/image`),
    onSuccess: (_response, variables) => {
      invalidateResourceImageQueries(variables.resourceId);
    },
  });
};
